import React, { useState } from "react";
import { Popover } from "@mui/material";

interface ChipsCellProps {
  /** Full list of values for the cell (e.g. customer names). */
  items?: string[];
  /** Column label used as the overflow popover heading. */
  header?: string;
  /** How many chips to show inline before collapsing the rest into "+N". */
  maxChips?: number;
  /** Rendered when there are no items. */
  emptyLabel?: string;
}

/**
 * Table cell that renders a list of chips, collapsing the overflow into a
 * clickable "+N" pill. Clicking it opens a popover listing the remaining items.
 *
 * Self-contained and column-agnostic so any listing can reuse it via the
 * `chips` column config — the shared Table simply delegates to it.
 */
const ChipsCell: React.FC<ChipsCellProps> = ({
  items,
  header = "Items",
  maxChips = 3,
  emptyLabel = "--",
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const list = (Array.isArray(items) ? items : []).filter(Boolean);

  if (!list.length) {
    return (
      <div className="cellText">
        <span className="cellText__dark">{emptyLabel}</span>
      </div>
    );
  }

  const visibleChips = list.slice(0, maxChips);
  const overflowChips = list.slice(maxChips);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  return (
    <div className="tableChips">
      {visibleChips.map((chip, index) => (
        <span className="tableChips__item" key={`${chip}-${index}`}>
          {chip}
        </span>
      ))}

      {overflowChips.length > 0 && (
        <>
          <button
            type="button"
            className="tableChips__more"
            onClick={handleOpen}
            aria-haspopup="dialog"
            aria-label={`Show ${overflowChips.length} more ${header}`}
          >
            +{overflowChips.length}
          </button>

          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            slotProps={{ paper: { className: "tableChipsPopover" } }}
          >
            <p className="tableChipsPopover__title">
              {header} ({overflowChips.length})
            </p>
            <div className="tableChipsPopover__list">
              {overflowChips.map((chip, index) => (
                <span className="tableChips__item" key={`${chip}-${index}`}>
                  {chip}
                </span>
              ))}
            </div>
          </Popover>
        </>
      )}
    </div>
  );
};

export default ChipsCell;
